import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, Wind, Wrench, Nfc, Search as SearchIcon, Package, ArrowRight, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const CATEGORIES = [
  { key: "civil_construction", label: "Civil Construction", icon: HardHat, cls: "from-amber-600 to-orange-700" },
  { key: "pollution_control",  label: "Pollution Control",  icon: Wind,    cls: "from-emerald-600 to-teal-700" },
  { key: "tools",              label: "Tools",              icon: Wrench,  cls: "from-slate-700 to-stone-900" },
  { key: "nfc_kit",            label: "NFC · RFID Kits",    icon: Nfc,     cls: "from-teal-700 to-emerald-900" },
];

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Products() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState({ mode: "buy", quantity: 1, duration_days: 1, city: "", delivery_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [myQuotes, setMyQuotes] = useState([]);

  useSeo({ title: "Construction & Pollution Products · find2near",
           description: "Buy or rent cranes, mixers, ETPs, scrubbers and 20+ B2B equipment SKUs.", canonical: "/products" });

  useEffect(() => {
    api.get("/products/construction", { params: { category: cat || undefined, q: q || undefined } })
       .then((r) => setItems(r.data));
  }, [cat, q]);

  useEffect(() => {
    if (user) api.get("/products/construction/quote/mine").then((r) => setMyQuotes(r.data)).catch(() => {});
  }, [user]);

  const submitQuote = async () => {
    if (!user) { toast.error("Sign in to request a quote"); return; }
    setBusy(true);
    try {
      await api.post("/products/construction/quote", { ...quote, sku: selected.sku });
      toast.success("Quote requested — we'll email you shortly");
      setSelected(null);
      api.get("/products/construction/quote/mine").then((r) => setMyQuotes(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const bySub = useMemo(() => {
    const groups = {};
    items.forEach((p) => { (groups[p.sub] = groups[p.sub] || []).push(p); });
    return groups;
  }, [items]);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="products-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Package size={12} /> B2B Marketplace</div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Civil Construction · <span className="text-emerald-700">Pollution Control</span></h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">Curated equipment from cranes to ETPs — get a same-day rental quote or a purchase estimate.</p>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px] rounded-full border border-stone-200 pl-3">
            <SearchIcon size={14} className="text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search — 'crane', 'ETP', 'mixer'…" className="flex-1 py-2 text-sm bg-transparent outline-none" data-testid="products-search" />
          </div>
          <button onClick={() => setCat("")} className={`text-xs font-bold px-3 py-1.5 rounded-full ${!cat ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${cat === c.key ? `bg-gradient-to-r ${c.cls} text-white` : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`products-cat-${c.key}`}>
              <c.icon size={13} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      {Object.entries(bySub).map(([sub, arr]) => (
        <div key={sub} className="mb-6" data-testid={`products-sub-${sub}`}>
          <h2 className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-2">{sub.replace(/_/g, " ")}</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {arr.map((p) => (
              <div key={p.sku} className="rounded-2xl bg-white border border-stone-200 p-4 flex items-start gap-3" data-testid={`product-${p.sku}`}>
                <div className="w-12 h-12 rounded-xl bg-stone-100 grid place-items-center shrink-0"><Package size={22} className="text-stone-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900">{p.name}</div>
                  <div className="text-[11px] text-stone-500 truncate">{Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                  <div className="text-xs text-stone-700 mt-1">
                    {p.rent_inr > 0 && <span>Rent <b>{fmt(p.rent_inr)}</b>/{p.unit} · </span>}
                    Buy <b>{fmt(p.buy_inr)}</b>
                  </div>
                </div>
                <button onClick={() => setSelected(p)} className="text-emerald-700 hover:text-emerald-900 text-sm font-bold inline-flex items-center gap-1" data-testid={`product-quote-${p.sku}`}>
                  Quote <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {myQuotes.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 p-5 mt-8" data-testid="products-my-quotes">
          <h3 className="heading text-base font-black text-stone-900 mb-3 flex items-center gap-2"><Truck size={16} /> My quotes</h3>
          <ul className="space-y-2">
            {myQuotes.map((q) => (
              <li key={q.quote_id} className="flex items-center justify-between text-sm bg-stone-50 rounded-xl px-3 py-2" data-testid={`quote-${q.quote_id}`}>
                <div>
                  <div className="font-bold text-stone-900">{q.product_name}</div>
                  <div className="text-[11px] text-stone-500">{q.mode} · qty {q.quantity}{q.duration_days ? ` · ${q.duration_days}d` : ""} · {new Date(q.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-black text-emerald-700 tabular-nums">{fmt(q.estimated_inr)}</div>
                  <div className="text-[10px] uppercase text-stone-400 font-bold">{q.status}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => setSelected(null)} data-testid="quote-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading text-xl font-black text-stone-900 mb-1">{selected.name}</h3>
            <div className="text-xs text-stone-500 mb-3">Get an estimate — our team confirms within 24h.</div>
            <div className="grid gap-2">
              <div className="grid grid-cols-2 gap-2">
                {["buy", ...(selected.rent_inr > 0 ? ["rent"] : [])].map((m) => (
                  <button key={m} onClick={() => setQuote({ ...quote, mode: m })} className={`py-2 rounded-full text-xs font-bold ${quote.mode === m ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-700"}`} data-testid={`quote-mode-${m}`}>{m}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={quote.quantity} onChange={(e) => setQuote({ ...quote, quantity: Number(e.target.value) })} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" placeholder="Qty" data-testid="quote-quantity" />
                {quote.mode === "rent" && <input type="number" value={quote.duration_days} onChange={(e) => setQuote({ ...quote, duration_days: Number(e.target.value) })} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" placeholder="Days" data-testid="quote-days" />}
              </div>
              <input value={quote.city} onChange={(e) => setQuote({ ...quote, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="quote-city" />
              <input type="date" value={quote.delivery_date} onChange={(e) => setQuote({ ...quote, delivery_date: e.target.value })} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="quote-date" />
              <textarea value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} rows={2} placeholder="Notes (site conditions, urgency…)" className="px-3 py-2 rounded-2xl border border-stone-200 text-sm outline-none" data-testid="quote-notes" />
              <button onClick={submitQuote} disabled={busy} className="mt-1 bg-emerald-700 hover:bg-emerald-800 text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="quote-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Request quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
